import { z } from 'zod';
import { Category } from './domain.js';
/**
 * The ranking rules, as pure functions over rows.
 *
 * These live in `contracts` rather than in the API because both sides need
 * them and they must not disagree: the grid reorders rows in the browser and
 * saves the result as a draft, and the server re-derives the same order when it
 * promotes that draft into `adjudications`. One implementation, one test suite.
 *
 * A ranking is an ordered list. Rank is the position, contiguous from 1, and
 * the order of categories along the list is fixed — first choices, then
 * reserves, then not selected. That makes "a reserve cannot be placed above a
 * first choice" a property of the ordering rather than a rule to remember.
 */
export const CATEGORY_ORDER = {
    first_choice: 0,
    reserve: 1,
    not_selected: 2,
};
/** Uncategorised sorts below everything: it is not a category, it is unfinished. */
export function categoryOrder(category) {
    return category ? CATEGORY_ORDER[category] : 3;
}
const UNPLACED = Number.MAX_SAFE_INTEGER;
/**
 * Canonical order: by category, then by the rank the row currently holds, then
 * by the order it arrived in. The last tie-break is what keeps a reorder
 * stable when several rows have no rank yet.
 */
export function order(rows) {
    return rows
        .map((row, index) => ({ row, index }))
        .sort((a, b) => categoryOrder(a.row.category) - categoryOrder(b.row.category)
        || (a.row.rank ?? UNPLACED) - (b.row.rank ?? UNPLACED)
        || a.index - b.index)
        .map((entry) => entry.row);
}
/** Canonical order with contiguous ranks from 1. */
export function renumber(rows) {
    return order(rows).map((row, index) => ({ ...row, rank: index + 1 }));
}
/**
 * Move one row up or down within its own category.
 *
 * A move past the end of its category is refused rather than applied, because
 * the only way to cross that boundary is to change the category — and doing
 * that silently would change a decision someone made, not just its position.
 */
export function move(rows, applicationId, delta) {
    const ordered = renumber(rows);
    const from = ordered.findIndex((r) => r.applicationId === applicationId);
    if (from < 0)
        return { rows: ordered, moved: false, rank: null };
    const self = ordered[from];
    const to = from + delta;
    const other = ordered[to];
    if (!other || other.category !== self.category) {
        return { rows: ordered, moved: false, rank: self.rank };
    }
    const next = [...ordered];
    next[from] = other;
    next[to] = self;
    return {
        rows: next.map((row, index) => ({ ...row, rank: index + 1 })),
        moved: true,
        rank: to + 1,
    };
}
/**
 * Set a rank by typing a number — the keyboard path that does not require
 * repeated presses to move a long way.
 *
 * The target is clamped to the span its own category occupies, so typing 1 for
 * a reserve moves it to the top of the reserves rather than above a first
 * choice.
 */
export function setRank(rows, applicationId, rank) {
    const ordered = renumber(rows);
    const from = ordered.findIndex((r) => r.applicationId === applicationId);
    if (from < 0)
        return { rows: ordered, moved: false, rank: null };
    const self = ordered[from];
    const block = ordered.filter((r) => r.category === self.category);
    const first = ordered.indexOf(block[0]);
    const last = ordered.indexOf(block[block.length - 1]);
    const target = Math.min(Math.max(rank - 1, first), last);
    if (target === from)
        return { rows: ordered, moved: false, rank: self.rank };
    const rest = ordered.filter((_, index) => index !== from);
    rest.splice(target, 0, self);
    return {
        rows: rest.map((row, index) => ({ ...row, rank: index + 1 })),
        moved: true,
        rank: target + 1,
    };
}
/** Thrown, not silently reclassified — the rule is explicit about that. */
export class ReserveNotWilling extends Error {
    applicationId;
    constructor(applicationId) {
        super('This applicant said they did not want to go on the reserve list.');
        this.name = 'ReserveNotWilling';
        this.applicationId = applicationId;
    }
}
/**
 * Change a row's category, placing it at the end of its new block. Ranks
 * renumber across the whole section, so a recategorised rank 1 does not leave
 * a hole where it was.
 */
export function recategorise(rows, applicationId, category, facts) {
    if (category === 'reserve' && !facts.reserveListWilling) {
        throw new ReserveNotWilling(applicationId);
    }
    const moved = rows.map((row) => row.applicationId === applicationId
        ? { ...row, category, rank: UNPLACED }
        : row);
    const out = renumber(moved);
    const placed = out.find((r) => r.applicationId === applicationId);
    return { rows: out, moved: true, rank: placed?.rank ?? null };
}
/* ── upload preconditions ─────────────────────────────────────────────────── */
export const UploadProblem = z.enum([
    'missing_category', 'missing_part', 'missing_score', 'missing_rank',
    'rank_1_not_first_choice', 'missing_subsection', 'reserve_not_willing',
]);
const PLACED = ['first_choice', 'reserve'];
/**
 * Every unmet precondition, not the first.
 *
 * An adjudicator with three sections should not discover the requirements one
 * round trip at a time, so this returns the complete list and the button's
 * disabled state is a convenience over the same answer.
 */
export function uploadBlockers(input) {
    const blockers = [];
    const willing = new Map(input.applicants.map((a) => [a.applicationId, a.reserveListWilling]));
    for (const section of input.sections) {
        const rows = input.rowsBySection[section.id] ?? [];
        if (!rows.length)
            continue;
        const add = (problem, ids) => {
            if (ids.length)
                blockers.push({ sectionId: section.id, problem, applicationIds: ids });
        };
        add('missing_category', rows.filter((r) => !r.category).map((r) => r.applicationId));
        /*
         * A part is required only where the team configured a list to choose from
         * — the violin grid has no parts, which is why the spec's violin rule does
         * not mention them — and then only of the first choices.
         *
         * Not of the reserves, and not of the people who were not selected. A part
         * is a place in the section, and which place somebody takes is not settled
         * until the section is: one first choice declining moves everybody below
         * them, and a reserve's part is decided when they are offered the place,
         * not months earlier by an adjudicator guessing at a line-up that does not
         * exist yet. Asking for it up front made an adjudicator invent answers
         * that would be wrong by the time anybody read them.
         */
        if (section.parts.length) {
            const allowed = new Set(partOptions(section.parts, section.doublingInstrument));
            add('missing_part', rows.filter((r) => r.category === 'first_choice' && (!r.part || !allowed.has(r.part)))
                .map((r) => r.applicationId));
        }
        // Nothing here keys off the word "percussion".
        if (section.scoring === 'part_and_score') {
            add('missing_score', rows.filter((r) => r.score === null || r.score === undefined).map((r) => r.applicationId));
        }
        add('reserve_not_willing', rows.filter((r) => r.category === 'reserve' && willing.get(r.applicationId) === false)
            .map((r) => r.applicationId));
        if (section.assignsSubsection) {
            const allowed = new Set(section.subsectionIds);
            // Only the people who got a place need a subsection: an unsuccessful
            // violinist is not assigned to Violin 1 or Violin 2.
            const placed = rows.filter((r) => PLACED.includes(r.category));
            add('missing_subsection', placed.filter((r) => !r.sectionId || !allowed.has(r.sectionId)).map((r) => r.applicationId));
            add('missing_rank', placed.filter((r) => r.rank === null).map((r) => r.applicationId));
            for (const subsection of section.subsectionIds) {
                const group = placed.filter((r) => r.sectionId === subsection);
                blockers.push(...rankOneBlockers(section.id, group));
            }
        }
        else {
            add('missing_rank', rows.filter((r) => r.rank === null).map((r) => r.applicationId));
            blockers.push(...rankOneBlockers(section.id, rows));
        }
    }
    return blockers;
}
/**
 * Rank 1 has to be a first choice. Skipped while anything in the group is still
 * uncategorised, because then the complaint would only repeat
 * `missing_category` in more confusing words.
 */
function rankOneBlockers(sectionId, group) {
    if (!group.length || group.some((r) => !r.category))
        return [];
    const top = order(group)[0];
    if (top.category === 'first_choice')
        return [];
    return [{ sectionId, problem: 'rank_1_not_first_choice', applicationIds: [top.applicationId] }];
}
/**
 * The count check the grid warns on. A warning, not a blocker: the team may
 * deliberately over- or under-select.
 */
export function firstChoiceWarning(rows, placesRequired) {
    const count = rows.filter((r) => r.category === 'first_choice').length;
    return { count, placesRequired, matches: count === placesRequired };
}
const WILLING = ['would_like', 'open_to'];
/**
 * Who a section's principal should be, from the approved ranking and what each
 * player said about the role.
 *
 * Rank 1, unless rank 1 would rather not — then the next-ranked player who
 * would like to or is open to it. If nobody in the section wants it, rank 1
 * again with the conflict flagged: a section needs a principal, and a person
 * can still decline the role while keeping their place.
 *
 * Pure, and here rather than in the API because the screen that shows the
 * proposal has to explain the same reasoning.
 */
export function choosePrincipal(players) {
    const chosen = players.find((p) => p.role === 'principal');
    if (chosen) {
        return {
            applicationId: chosen.application_id,
            reason: 'manual_swap',
            conflict: chosen.principal_preference === 'would_rather_not',
        };
    }
    const top = players[0];
    if (top.principal_preference !== 'would_rather_not') {
        return { applicationId: top.application_id, reason: 'rank_1', conflict: false };
    }
    const willing = players.find((p) => WILLING.includes(p.principal_preference));
    if (willing) {
        return {
            applicationId: willing.application_id,
            reason: 'rank_1_declined_preference',
            conflict: false,
        };
    }
    return { applicationId: top.application_id, reason: 'rank_1_declined_preference', conflict: true };
}
/* ── parts, and the doubling that may come with one ────────────────────────── */
/**
 * Every part somebody in this section could be given.
 *
 * A section's parts are its positions — 1st flute, 2nd flute — and the
 * doubling instrument is not one of them: piccolo is not a chair, it is
 * something the person in a chair also plays. Listing it as a part meant an
 * adjudicator had to choose between "2nd flute" and "piccolo" for somebody who
 * does both.
 *
 * So the doubling is attached to a part rather than replacing one, and it can
 * be attached to any of them. Which chair doubles is not fixed — it is usually
 * the last desk in the woodwind, and among the low brass it often is not — so
 * this offers the choice everywhere rather than guessing.
 *
 * One function because both sides depend on it: the chooser lists these and
 * the upload refuses anything that is not one of them.
 */
export function partOptions(parts, doublingInstrument) {
    if (!doublingInstrument?.trim())
        return [...parts];
    const doubling = doublingInstrument.trim();
    return parts.flatMap((part) => [part, `${part} + ${doubling}`]);
}
//# sourceMappingURL=ranking.js.map